<template>
  <div style="overflow-y: auto;">
    <!-- Header bar -->
    <div class="w-100 p-3 d-flex align-items-center justify-content-between bg-light border-bottom">
      <strong>{{ $t("js.backend.ballot_preview.interactive_preview", {}, { locale: backendLocale }) }}</strong>
      <select class="btn btn-sm btn-light dropdown-toggle" v-model="selectedLocale">
        <option v-for="loc in availableLocales" :key="loc" :value="loc">
          {{ $t("js.language_name", {}, { locale: loc }) }}
        </option>
      </select>
    </div>

    <!-- Simulated frontend shell -->
    <div class="p-0 m-0 bg-light">
      <div class="container p-0 px-md-5 pt-sm-3 pt-md-5 pb-5">
        <div class="card border-0 rounded-0 shadow-lg h-100 mb-3">
          <!-- Election header -->
          <div
            class="card-header no-accent d-flex flex-column flex-sm-row align-items-stretch bg-theme rounded-0 border-bottom align-items-sm-center p-0"
          >
            <div v-if="themeImg" class="bg-light p-3 align-self-stretch d-flex align-items-center">
              <img :src="themeImg" class="me-3" style="max-height: 50px; max-width: 100%" />
            </div>
            <div class="d-flex flex-grow-1 align-items-center p-3">
              <h3 class="mb-0">{{ localValue(electionTitle) }}</h3>
            </div>
          </div>

          <!-- Information page content -->
          <div class="card-body h-100 p-3 p-md-5" :dir="isRtl ? 'rtl' : 'ltr'">
            <h2 class="mb-4">{{ localValue(title) }}</h2>
            <div class="information-page-content" v-html="localValue(content)" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { rtlLanguages } from "@/entrypoints/shared/i18n_constants";
import type { PropType, LocalString, SupportedLocale } from "@/types";

const { t } = useI18n();

const props = defineProps({
  title: {
    type: Object as PropType<LocalString>,
    required: true,
  },
  content: {
    type: Object as PropType<LocalString>,
    required: true,
  },
  electionTitle: {
    type: Object as PropType<LocalString>,
    required: true,
  },
  availableLocales: {
    type: Array as PropType<SupportedLocale[]>,
    default: () => ["en"],
  },
  initialLocale: {
    type: String as PropType<SupportedLocale>,
    default: "en",
  },
  backendLocale: {
    type: String as PropType<SupportedLocale>,
    default: "en",
  },
  themeImg: {
    type: String,
    default: null,
  },
});

const selectedLocale = ref<SupportedLocale>(props.initialLocale);

const isRtl = computed(() => rtlLanguages.includes(selectedLocale.value));

const localValue = (obj: LocalString) =>
  obj?.[selectedLocale.value] ?? obj?.[props.availableLocales[0]] ?? "";
</script>